import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api, listAllResource, listResource } from '@/lib/api';
import { matchesSearch } from '@/lib/search-normalize';
import { credentialSuccessToast } from '@/lib/user-feedback';

interface IssueForm {
  userId: string;
  templateId: string;
  courseId: string;
}

interface Option {
  id: string;
  name: string;
  fullName?: string;
  email?: string;
  title?: string;
  code?: string;
  category?: { name?: string };
}

const FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white py-3 px-4 text-base outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

export function CredentialIssuePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [courseSearch, setCourseSearch] = useState('');
  const { register, handleSubmit, watch, setValue } = useForm<IssueForm>({
    defaultValues: { userId: '', templateId: '', courseId: '' },
  });
  const selectedUserId = watch('userId');
  const selectedCourseId = watch('courseId');

  const { data: users, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users'],
    queryFn: () => listAllResource<Option>('/users'),
  });
  const { data: templates } = useQuery({
    queryKey: ['credential-templates'],
    queryFn: () => listResource<Option>('/credentials/templates'),
  });
  const { data: courses, isLoading } = useQuery({
    queryKey: ['courses', 'all'],
    queryFn: () => listAllResource<Option>('/courses'),
  });

  const filteredUsers = useMemo(() => {
    const list = users?.data ?? [];
    if (!search.trim()) return list;
    return list.filter(
      (u) => matchesSearch(u.fullName ?? u.name, search) || matchesSearch(u.email, search),
    );
  }, [users?.data, search]);

  const filteredCourses = useMemo(() => {
    const list = courses?.data ?? [];
    if (!courseSearch.trim()) return list;
    return list.filter(
      (c) =>
        matchesSearch(c.name ?? c.title, courseSearch) ||
        matchesSearch(c.code, courseSearch) ||
        matchesSearch(c.category?.name, courseSearch),
    );
  }, [courses?.data, courseSearch]);

  const mutation = useMutation({
    mutationFn: (values: IssueForm) => api('/credentials/issue', { method: 'POST', body: values }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['credentials'] });
      toast(credentialSuccessToast('issue'));
      navigate('/credenciales');
    },
  });

  return (
    <div className="lms-credentials-issue lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink to="/credenciales" label="Volver a credenciales emitidas" />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Emisión manual</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Emitir credencial a un estudiante por curso
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <form
          onSubmit={handleSubmit((v) => mutation.mutate(v))}
          className="lms-credentials-glass-panel w-full rounded-xl border border-dash-outline-variant p-6 shadow-sm md:p-8"
        >
          {mutation.error && (
            <p className="mb-4 text-sm text-red-600" role="alert">
              {(mutation.error as Error).message}
            </p>
          )}
          <div className="space-y-4">
            <div className="space-y-3">
              <input type="hidden" {...register('userId', { required: true })} />
              <label htmlFor="user-search" className="text-sm font-medium text-dash-on-surface-variant">
                Estudiante <span className="text-error-600">*</span>
              </label>

              <div className="relative">
                <span
                  className="material-symbols-outlined pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-dash-on-surface-variant"
                  aria-hidden="true"
                >
                  search
                </span>
                <input
                  id="user-search"
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar estudiante por nombre o correo..."
                  className="h-10 w-full rounded-lg border border-dash-outline-variant bg-secondary-50 pl-12 pr-4 text-sm outline-none transition-all focus:border-dash-secondary focus:bg-white focus:ring-2 focus:ring-dash-secondary/20"
                />
              </div>

              {isLoadingUsers ? (
                <div className="flex justify-center py-8">
                  <Loader />
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-dash-outline-variant bg-white">
                  <div className="max-h-64 overflow-y-auto p-1">
                    <div className="space-y-1">
                      {filteredUsers.length === 0 ? (
                        <p className="px-3 py-6 text-center text-sm text-dash-on-surface-variant">
                          No hay estudiantes que coincidan con la búsqueda.
                        </p>
                      ) : (
                        filteredUsers.map((u) => {
                          const checked = selectedUserId === u.id;
                          const displayName = u.fullName ?? u.name;
                          return (
                            <label
                              key={u.id}
                              className={`flex cursor-pointer items-center gap-4 rounded-md p-3 transition-colors hover:bg-secondary-50 ${
                                checked ? 'bg-brand-50' : ''
                              }`}
                            >
                              <input
                                type="radio"
                                name="student_choice"
                                checked={checked}
                                onChange={() => setValue('userId', u.id, { shouldValidate: true })}
                                className="h-5 w-5 border-dash-outline-variant text-dash-primary focus:ring-dash-secondary/20"
                              />
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-dash-on-surface-variant">
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  person
                                </span>
                              </div>
                              <div className="min-w-0 flex-grow">
                                <p className="text-sm font-medium text-dash-on-surface">{displayName}</p>
                                {u.email && (
                                  <p className="truncate text-xs text-dash-on-surface-variant">{u.email}</p>
                                )}
                              </div>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-3">
              <input type="hidden" {...register('courseId', { required: true })} />
              <label htmlFor="course-search" className="text-sm font-medium text-dash-on-surface-variant">
                Curso <span className="text-error-600">*</span>
              </label>

              <div className="relative">
                <span
                  className="material-symbols-outlined pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-dash-on-surface-variant"
                  aria-hidden="true"
                >
                  search
                </span>
                <input
                  id="course-search"
                  type="search"
                  value={courseSearch}
                  onChange={(e) => setCourseSearch(e.target.value)}
                  placeholder="Buscar curso por nombre o código..."
                  className="h-10 w-full rounded-lg border border-dash-outline-variant bg-secondary-50 pl-12 pr-4 text-sm outline-none transition-all focus:border-dash-secondary focus:bg-white focus:ring-2 focus:ring-dash-secondary/20"
                />
              </div>

              <div className="overflow-hidden rounded-lg border border-dash-outline-variant bg-white">
                <div className="max-h-64 overflow-y-auto p-1">
                  <div className="space-y-1">
                    {filteredCourses.length === 0 ? (
                      <p className="px-3 py-6 text-center text-sm text-dash-on-surface-variant">
                        No hay cursos que coincidan con la búsqueda.
                      </p>
                    ) : (
                      filteredCourses.map((c) => {
                        const checked = selectedCourseId === c.id;
                        const displayName = c.name ?? c.title;
                        const subtitle = [c.code, c.category?.name].filter(Boolean).join(' · ');
                        return (
                          <label
                            key={c.id}
                            className={`flex cursor-pointer items-center gap-4 rounded-md p-3 transition-colors hover:bg-secondary-50 ${
                              checked ? 'bg-brand-50' : ''
                            }`}
                          >
                            <input
                              type="radio"
                              name="course_choice"
                              checked={checked}
                              onChange={() => setValue('courseId', c.id, { shouldValidate: true })}
                              className="h-5 w-5 border-dash-outline-variant text-dash-primary focus:ring-dash-secondary/20"
                            />
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-dash-on-surface-variant">
                              <span className="material-symbols-outlined" aria-hidden="true">
                                school
                              </span>
                            </div>
                            <div className="min-w-0 flex-grow">
                              <p className="text-sm font-medium text-dash-on-surface">{displayName}</p>
                              {subtitle && (
                                <p className="truncate text-xs text-dash-on-surface-variant">{subtitle}</p>
                              )}
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <label htmlFor="templateId" className="text-sm font-medium text-dash-on-surface-variant">
                Plantilla <span className="text-error-600">*</span>
              </label>
              <select id="templateId" required className={FIELD} {...register('templateId', { required: true })}>
                <option value="">Seleccionar plantilla</option>
                {(templates?.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name ?? t.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button
            type="submit"
            disabled={mutation.isPending || !selectedUserId || !selectedCourseId}
            className="mt-8 w-full rounded-lg bg-dash-primary px-6 py-3 text-sm font-medium text-white disabled:opacity-60"
          >
            Emitir credencial
          </button>
          <Link to="/credenciales" className="mt-3 block text-center text-sm text-dash-secondary hover:underline">
            Cancelar
          </Link>
        </form>
      )}
    </div>
  );
}
